import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Typography,
    Grid,
    IconButton,
    Divider,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
} from "@mui/material";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import authService from "../../Auth/AuthService";

const emptyMark = { subjectName: "", marksObtained: "", maxMark: "", grade: "", remark: "" };

const EXAM_TYPES = ["First Term", "Second Term", "Final Exam"];

const ACADEMIC_YEAR_REGEX = /^(\d{4})-(\d{4})$/;

const isAcademicYearValid = (value) => {
    const match = ACADEMIC_YEAR_REGEX.exec(value || "");
    if (!match) return false;
    // Academic years are two consecutive years, e.g. 2026-2027.
    return Number(match[2]) === Number(match[1]) + 1;
};

const MarksheetForm = ({ marksheets, classRoomid, onSuccess }) => {
    const [form, setForm] = useState({
        classRoomId: classRoomid,
        examType: "",
        academicYear: "",
        remarks: "",
        marks: [{ ...emptyMark }],
    });

    useEffect(() => {
        if (marksheets) {
            setForm(...marksheets)
        }
    }, [marksheets]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleMarkChange = (index, e) => {
        const { name, value } = e.target;

        // Cap marksObtained/maxMark at 3 digits (0-999); reject the
        // keystroke entirely rather than accepting then erroring later.
        if ((name === "marksObtained" || name === "maxMark") && !/^\d{0,3}$/.test(value)) {
            return;
        }

        const updatedMarks = [...form.marks];
        updatedMarks[index][name] = value;
        setForm({ ...form, marks: updatedMarks });
    };

    const addSubject = () => {
        setForm({
            ...form,
            marks: [...form.marks, { ...emptyMark }],
        });
    };

    const removeSubject = (index) => {
        const updatedMarks = form.marks.filter((_, i) => i !== index);
        setForm({ ...form, marks: updatedMarks });
    };

    // A mark row is valid once subject, obtained marks and max marks are
    // filled in, obtained/max are real numbers, and obtained doesn't
    // exceed max. Remark stays optional (placeholder says "or Non").
    const isMarkRowValid = (mark) => {
        const subjectOk = mark.subjectName?.trim().length > 0;

        const obtainedOk =
            mark.marksObtained !== "" &&
            mark.marksObtained !== null &&
            !isNaN(mark.marksObtained) &&
            Number(mark.marksObtained) >= 0;

        const maxOk =
            mark.maxMark !== "" &&
            mark.maxMark !== null &&
            !isNaN(mark.maxMark) &&
            Number(mark.maxMark) > 0 &&
            Number(mark.maxMark) <= 999;

        const obtainedInRangeOk =
            obtainedOk && Number(mark.marksObtained) <= 999;

        const withinRangeOk =
            obtainedInRangeOk && maxOk
                ? Number(mark.marksObtained) <= Number(mark.maxMark)
                : true;

        return subjectOk && obtainedInRangeOk && maxOk && withinRangeOk;
    };

    const isFormValid =
        form.examType?.trim().length > 0 &&
        isAcademicYearValid(form.academicYear) &&
        form.remarks?.trim().length > 0 &&
        form.marks.length > 0 &&
        form.marks.every(isMarkRowValid);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid) return;

        try {
            if (marksheets) {
                const formatted = marksheets.map(m => ({
                    id: m.id,
                    academicYear: m.academicYear,
                    examType: form?.examType,
                    classRoomId: m.classRoom?.classId || null,
                    marks: m.marks,
                    remarks: m.remarks,
                }));
                const url = authService.pathUrl('PUT', '/marksheets');
                authService.putAuth(url, ...formatted);
            } else {
                const url = authService.pathUrl('POST', '/marksheets');
                authService.postAuth(url, form);
            }
            // alert("Marksheet saved successfully");
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            alert("Error saving marksheet");
        }
    };

    return (
        <Box>
            {/* <Paper sx={{ p: 2 }}> */}
            {/* <Typography variant="h5" gutterBottom>
                    {marksheetId ? "Update Marks" : "Add Marks"}
                </Typography> */}
            <Divider sx={{ mb: 3 }} />
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid size={4}>
                        <FormControl required fullWidth error={form.examType?.trim().length === 0}>
                            <InputLabel id="examType-label">Exam Type</InputLabel>
                            <Select
                                labelId="examType-label"
                                label="Exam Type"
                                name="examType"
                                value={form.examType}
                                onChange={handleChange}
                            >
                                {EXAM_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {form.examType?.trim().length === 0 ? "Required" : " "}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            required
                            label="Academic Year"
                            name="academicYear"
                            placeholder="Example: 2026-2027"
                            value={form.academicYear}
                            onChange={handleChange}
                            error={form.academicYear.length > 0 && !isAcademicYearValid(form.academicYear)}
                            helperText={
                                form.academicYear.trim().length === 0
                                    ? "Required"
                                    : !isAcademicYearValid(form.academicYear)
                                        ? "Format: YYYY-YYYY, consecutive years (e.g. 2026-2027)"
                                        : " "
                            }
                            fullWidth
                        />
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            required
                            label="Remarks"
                            name="remarks"
                            placeholder="Example: Excellent performance"
                            value={form.remarks}
                            onChange={handleChange}
                            error={form.remarks?.trim().length === 0}
                            helperText={form.remarks?.trim().length === 0 ? "Required" : " "}
                            fullWidth
                        // multiline
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mt: 3 }}>
                    Subjects & Marks
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {form.marks.map((mark, index) => {
                    const obtainedNum = Number(mark.marksObtained);
                    const maxNum = Number(mark.maxMark);
                    const exceedsMax =
                        mark.marksObtained !== "" &&
                        mark.maxMark !== "" &&
                        !isNaN(obtainedNum) &&
                        !isNaN(maxNum) &&
                        obtainedNum > maxNum;

                    return (
                        <Grid container spacing={2} mb={2} alignItems="center" key={index}>
                            <Grid size={2}>
                                <TextField
                                    required
                                    label="Subject"
                                    name="subjectName"
                                    value={mark.subjectName}
                                    onChange={(e) => handleMarkChange(index, e)}
                                    error={mark.subjectName?.trim().length === 0}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={2}>
                                <TextField
                                    required
                                    label="Obtained"
                                    name="marksObtained"
                                    type="number"
                                    value={mark.marksObtained}
                                    onChange={(e) => handleMarkChange(index, e)}
                                    error={mark.marksObtained === "" || exceedsMax}
                                    // helperText={exceedsMax ? "Exceeds max" : " "}
                                    slotProps={{ htmlInput: { maxLength: 3, min: 0, max: 999 } }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={2}>
                                <TextField
                                    required
                                    label="Max Mark"
                                    name="maxMark"
                                    type="number"
                                    value={mark.maxMark}
                                    onChange={(e) => handleMarkChange(index, e)}
                                    error={mark.maxMark === "" || Number(mark.maxMark) <= 0 || Number(mark.maxMark) > 999}
                                    slotProps={{ htmlInput: { maxLength: 3, min: 0, max: 999 } }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={3}>
                                <TextField
                                    placeholder="Write Remark or Non"
                                    label="Remark/Non-academic"
                                    name="remark"
                                    value={mark.remark}
                                    onChange={(e) => handleMarkChange(index, e)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={1}>
                                <IconButton color="error" onClick={() => removeSubject(index)}>
                                    <RemoveCircle />
                                </IconButton>
                            </Grid>
                        </Grid>
                    );
                })}

                <Button startIcon={<AddCircle />} onClick={addSubject} sx={{ mt: 2, color: '#1b1914', fontWeight: 'bold' }}>
                    Add Subject
                </Button>

                <Box sx={{ mt: 3, ml: 2, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid}
                        sx={{ bgcolor: '#1b1914', borderRadius: 5 }}
                    >
                        Save
                    </Button>
                    {!isFormValid && (
                        <Typography variant="caption" color="error">
                            Fill in all required fields (subject, obtained and max marks) to save.
                        </Typography>
                    )}
                </Box>
            </form>
        </Box>
    );
};

export default MarksheetForm;